import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Edit2, X } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { getPlatformConfig, updatePlatformConfig } from '@/services/api.client';

function ConfigCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] p-4 space-y-3">
      <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
      {children}
    </div>
  );
}

function ConfigRow({
  label,
  value,
  editable,
  onSave,
}: {
  label: string;
  value: string;
  editable?: boolean;
  onSave?: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  return (
    <div className="flex items-center gap-2">
      <p className="text-xs text-[var(--text-secondary)] w-36 shrink-0">{label}</p>
      {editing ? (
        <>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 text-xs flex-1"
          />
          <Button
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => {
              onSave?.(draft);
              setEditing(false);
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-1"
            onClick={() => {
              setDraft(value);
              setEditing(false);
            }}
          >
            <X className="size-3" />
          </Button>
        </>
      ) : (
        <>
          <span className="text-sm text-[var(--text-primary)] flex-1 font-mono">{value}</span>
          {editable && (
            <Button
              size="sm"
              variant="ghost"
              aria-label="Edit"
              className="h-7 px-1"
              onClick={() => {
                setDraft(value);
                setEditing(true);
              }}
            >
              <Edit2 className="size-3" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function ApiKeyStatus({ status }: { status: string }) {
  const ok = status === 'configured';
  return (
    <div className="flex items-center gap-2">
      <p className="text-xs text-[var(--text-secondary)] w-36 shrink-0">API Key</p>
      <Badge variant={ok ? 'default' : 'destructive'} className="text-xs flex items-center gap-1">
        {ok && <CheckCircle2 className="size-3" />}
        {status}
      </Badge>
    </div>
  );
}

export function SuperAdminConfigPage() {
  const qc = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: ['admin', 'config'],
    queryFn: getPlatformConfig,
  });

  const mutation = useMutation({
    mutationFn: updatePlatformConfig,
    onSuccess: (updated) => {
      qc.setQueryData(['admin', 'config'], updated);
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">System Configuration</h1>
        <p className="text-sm text-[var(--text-secondary)]">Platform-level service configuration</p>
      </div>

      <ConfigCard title="SMS — Africa's Talking">
        <ConfigRow label="Active Provider" value={config.sms.primaryProvider} />
        <ConfigRow
          label="Sender ID"
          value={config.sms.africasTalking.senderId}
          editable
          onSave={(v) => mutation.mutate({ africasTalking: { senderId: v } })}
        />
        <ApiKeyStatus status={config.sms.africasTalking.status} />
      </ConfigCard>

      <ConfigCard title="SMS — Vonage">
        <ConfigRow label="From Number" value={config.sms.vonage.fromNumber} />
        <ApiKeyStatus status={config.sms.vonage.status} />
      </ConfigCard>

      <ConfigCard title="Resend">
        <ConfigRow
          label="From Name"
          value={config.resend.fromName}
          editable
          onSave={(v) => mutation.mutate({ resend: { fromName: v } })}
        />
        <ApiKeyStatus status={config.resend.apiKeyStatus} />
      </ConfigCard>

      <ConfigCard title="Google Maps">
        <ApiKeyStatus status={config.googleMaps.apiKeyStatus} />
      </ConfigCard>

      <ConfigCard title="Data Retention">
        <ConfigRow
          label="Default (days)"
          value={String(config.dataRetention.defaultRetentionDays)}
          editable
          onSave={(v) => {
            const n = parseInt(v, 10);
            if (!isNaN(n) && n > 0) mutation.mutate({ dataRetention: { defaultRetentionDays: n } });
          }}
        />
      </ConfigCard>

      <ConfigCard title="Privacy Policy">
        <ConfigRow label="Version" value={config.privacyPolicy.version} />
        <ConfigRow
          label="Updated"
          value={new Date(config.privacyPolicy.updatedAt).toLocaleDateString()}
        />
      </ConfigCard>

      <ConfigCard title="Terms of Service">
        <ConfigRow label="Version" value={config.termsOfService.version} />
        <ConfigRow
          label="Updated"
          value={new Date(config.termsOfService.updatedAt).toLocaleDateString()}
        />
      </ConfigCard>
    </div>
  );
}
